import { animate, AnimatePresence, LayoutGroup, motion, useMotionValue } from 'framer-motion'
import type { Transition, Variants } from 'framer-motion'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { throttle } from 'throttle-debounce'
import { twMerge } from 'tailwind-merge'
import {
  useMiniDragging,
  useSetMiniHovered,
  useMiniOnLeftSide,
  useMiniNotched,
  useSetMiniRecording,
  useSetMiniPaused,
  useSetMiniContentHovered,
  useIsMiniMenuOpen,
  useMiniPosition,
  useSetMiniPosition,
  miniDragControlsRef,
} from '../../atoms'
import { MiniContent } from './nested/content'
import { MiniLogo } from './nested/logo'
import { MiniDragHandle } from './nested/drag-handle'
import { MiniControls } from './nested/controls'
import { MiniFinishedControls } from './nested/finished-controls'
import { MiniMenu } from './nested/mini-menu'
import { MiniNotch } from './notch'
import { MiniNotchSnapRegion } from './notch/notch-snap-region'
import {
  getMiniNotchRegion,
  isWithinNotchSnapZone,
  MiniNotchRegionProvider,
  NOTCH_EXIT_DISTANCE,
} from './notch/notch-region'
import { useMiniContentHover } from './use-mini-content-hover'
import { useOverlayDisplaySnapshot } from '@renderer/overlay/hooks/use-overlay-notch-bounds'
import { Interactive } from '@highlight/ui'
import { sendOverlayDragPing } from '@renderer/overlay/utils/drag-ping'
import { useHoldOverlayDisplayKey } from '@renderer/overlay/hooks/use-overlay-display-key'
import { isMac } from '@shared/utils/platform'

const PILL_SIZE = 44
const DEFAULT_TOP_POSITION = 28
const HANDLE_INSET = 14 // Distance from element edge to drag handle center

// Hysteresis thresholds to prevent flip thrashing near center
const FLIP_THRESHOLD_TO_RIGHT = 0.51
const FLIP_THRESHOLD_TO_LEFT = 0.49
const NOTCH_UNHOVER_DELAY_MS = 200

const miniContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
}

const miniContainerTransition: Transition = {
  duration: 0.4,
}

const miniTransition: Transition = {
  type: 'spring',
  bounce: 0,
  duration: 0.4,
}

// Shared by the free pill and the notched shell; `custom` carries the dragging flag
const miniShellVariants: Variants = {
  hidden: { opacity: 0 },
  visible: (isDragging: boolean) => ({ opacity: 1, scale: isDragging ? 0.97 : 1 }),
}

const miniShellTransition: Transition = {
  opacity: { duration: 0.15 },
  scale: miniTransition,
}

const clampPosition = (pos: { x: number; y: number }, width: number, height: number) => ({
  x: Math.max(0, Math.min(pos.x, width - PILL_SIZE)),
  y: Math.max(0, Math.min(pos.y, height - PILL_SIZE)),
})

interface MiniProps {
  className?: string
  isRecording?: boolean
  isPaused?: boolean
  children?: ReactNode
}

function Mini({ className, isRecording = false, isPaused = false, children }: MiniProps) {
  const [isDragging, setDragging] = useMiniDragging()

  // Refs for custom drag implementation
  const isDraggingRef = useRef(false)
  const grabPointRef = useRef({ x: 0, y: 0 })
  const lastClientRef = useRef({ x: 0, y: 0 })
  const elementSizeRef = useRef({ width: PILL_SIZE, height: PILL_SIZE })
  const cleanupDragRef = useRef<(() => void) | null>(null)

  const setHovered = useSetMiniHovered()
  const [isOnLeftSide, setIsOnLeftSide] = useMiniOnLeftSide()
  const isOnLeftSideRef = useRef(isOnLeftSide)
  isOnLeftSideRef.current = isOnLeftSide

  const [isNotched, setNotched] = useMiniNotched()
  const isNotchedRef = useRef(isNotched)
  isNotchedRef.current = isNotched

  // True while a drag that began in the notch hasn't yet been pulled far enough to release
  const heldByNotchRef = useRef(false)
  const dragOriginRef = useRef({ x: 0, y: 0 })
  const [isSnapCandidate, setSnapCandidate] = useState(false)
  const snapCandidateRef = useRef(false)

  const setRecording = useSetMiniRecording()
  const setPaused = useSetMiniPaused()
  const setContentHovered = useSetMiniContentHovered()
  const isMenuOpen = useIsMiniMenuOpen()

  // Delay hover interactions after mount
  const [isMountDelayComplete, setIsMountDelayComplete] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setIsMountDelayComplete(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const contentRef = useRef<HTMLDivElement>(null)
  const didDragRef = useRef(false)
  const pendingFlipRef = useRef(false)
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })
  const prevWindowSizeRef = useRef(windowSize)

  const { notchBounds, menuBarHeight } = useOverlayDisplaySnapshot()
  const notchRegion = useMemo(
    () => getMiniNotchRegion(notchBounds, menuBarHeight, windowSize.width),
    [notchBounds, menuBarHeight, windowSize.width],
  )
  const notchRegionRef = useRef(notchRegion)
  notchRegionRef.current = notchRegion

  // Sync isRecording and isPaused props to atoms
  useEffect(() => {
    setRecording(isRecording)
  }, [isRecording, setRecording])

  useEffect(() => {
    setPaused(isPaused)
  }, [isPaused, setPaused])

  const defaultPosition = useMemo(
    () => ({
      x: windowSize.width / 2 - PILL_SIZE / 2,
      y: DEFAULT_TOP_POSITION,
    }),
    [windowSize.width],
  )

  // Per monitor, so moving the overlay to another display restores where Mini
  // was left there rather than dragging this display's position along.
  const storedPosition = useMiniPosition()
  const setStoredPosition = useSetMiniPosition()
  const holdDisplayKey = useHoldOverlayDisplayKey()

  const position = useMemo(
    () => clampPosition(storedPosition ?? defaultPosition, windowSize.width, windowSize.height),
    [storedPosition, defaultPosition, windowSize],
  )

  // Motion values for position (bypasses React re-renders during drag for performance)
  const motionX = useMotionValue(position.x)
  const motionY = useMotionValue(position.y)
  const isInitialMount = useRef(true)

  // Convert screen-left x coordinate to motionX value based on anchoring side.
  // When anchored left (left:0), motionX = screenX (positive, moves right).
  // When anchored right (right:0), motionX = -(windowWidth - screenX - elementWidth) (negative, moves left from right edge).
  const toMotionX = useCallback((screenX: number, elWidth: number = PILL_SIZE) => {
    if (isOnLeftSideRef.current) return screenX
    return -(window.innerWidth - screenX - elWidth)
  }, [])

  // Sync stored position → motion values (animate when not dragging, instant on mount/flip)
  useEffect(() => {
    if (isDraggingRef.current || isNotchedRef.current) return

    const elWidth = contentRef.current?.getBoundingClientRect().width ?? PILL_SIZE
    const targetX = toMotionX(position.x, elWidth)
    const isFlip = pendingFlipRef.current
    pendingFlipRef.current = false

    if (isInitialMount.current || isFlip) {
      // Set instantly on mount or after a side flip (CSS anchoring just changed)
      motionX.set(targetX)
      motionY.set(position.y)
      isInitialMount.current = false
    } else {
      // Only animate if position meaningfully differs from current motion values
      const currentX = motionX.get()
      const currentY = motionY.get()
      const xDiff = Math.abs(currentX - targetX)
      const yDiff = Math.abs(currentY - position.y)

      if (xDiff > 1 || yDiff > 1) {
        animate(motionX, targetX, miniTransition)
        animate(motionY, position.y, miniTransition)
      }
    }
  }, [position.x, position.y, motionX, motionY, isOnLeftSide, isNotched, toMotionX])

  // Keep Mini on screen after the overlay resizes (display switch, resolution
  // change, dock), measuring the real element rather than the collapsed pill —
  // an expanded recording Mini is much wider than PILL_SIZE, so `position`'s
  // own clamp isn't enough.
  //
  // Corrects the motion values only. Writing the clamped value back would
  // replace where the user parked Mini with a position derived from whatever
  // monitor the overlay happened to resize onto.
  useEffect(() => {
    if (isDraggingRef.current) return
    if (isNotchedRef.current) {
      // The notch positions Mini; just keep the baseline fresh for when it's pulled back out
      prevWindowSizeRef.current = windowSize
      return
    }

    // Use rAF so the DOM has settled after the resize before we measure
    const frameId = requestAnimationFrame(() => {
      const rect = contentRef.current?.getBoundingClientRect()
      if (!rect || rect.width === 0) return

      const prev = prevWindowSizeRef.current
      prevWindowSizeRef.current = windowSize

      // Convert current motionX back to screen-left coordinate using the OLD width
      // (motionX was set relative to the old window size, so using window.innerWidth
      // would double-count the width change for right-anchored Mini)
      const screenX = isOnLeftSideRef.current ? motionX.get() : prev.width + motionX.get() - rect.width
      const currentY = motionY.get()

      const maxX = Math.max(0, windowSize.width - rect.width)
      const maxY = Math.max(0, windowSize.height - rect.height)

      const clampedX = Math.max(0, Math.min(screenX, maxX))
      const clampedY = Math.max(0, Math.min(currentY, maxY))

      const clampedMotionX = toMotionX(clampedX, rect.width)

      // Only correct if meaningfully changed (avoid sub-pixel noise)
      if (Math.abs(clampedMotionX - motionX.get()) > 1 || Math.abs(clampedY - currentY) > 1) {
        animate(motionX, clampedMotionX, miniTransition)
        animate(motionY, clampedY, miniTransition)
      }
    })

    return () => cancelAnimationFrame(frameId)
  }, [windowSize, motionX, motionY, toMotionX])

  useEffect(() => {
    const handleResize = throttle(200, () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight })
    })
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      handleResize.cancel()
    }
  }, [])

  // Clean up drag listeners on unmount
  useEffect(() => {
    return () => {
      cleanupDragRef.current?.()
    }
  }, [])

  // Custom drag implementation - uses pointer events for robust cross-display dragging.
  // Position is always tracked as absolute {x, y} from the window's top-left corner,
  // which eliminates the ambiguity of left/right edge conventions.
  const startDrag = useCallback(
    (e: ReactPointerEvent | PointerEvent) => {
      // Guard against starting a new drag while one is already in progress
      // Mac keeps the Mini docked in the notch; Windows has no notch to dock into, so it stays draggable there
      if (isDraggingRef.current || isMac) return

      const rect = contentRef.current?.getBoundingClientRect()
      if (!rect) return

      // Record where within the element the user grabbed (grab point)
      grabPointRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }
      lastClientRef.current = { x: e.clientX, y: e.clientY }
      dragOriginRef.current = { x: e.clientX, y: e.clientY }
      elementSizeRef.current = { width: rect.width, height: rect.height }
      heldByNotchRef.current = isNotchedRef.current
      isDraggingRef.current = true
      setDragging(true)

      const updateSnapCandidate = (candidate: boolean) => {
        if (candidate === snapCandidateRef.current) return
        snapCandidateRef.current = candidate
        setSnapCandidate(candidate)
      }

      // The free pill mounts on the side nearest the cursor and is measured a frame later;
      // the notch keeps holding until then so no move runs against the zero-width shell.
      let releasingFromNotch = false
      const releaseFromNotch = (ev: PointerEvent) => {
        releasingFromNotch = true
        const newIsLeft = ev.clientX < window.innerWidth / 2
        isOnLeftSideRef.current = newIsLeft
        pendingFlipRef.current = true
        setIsOnLeftSide(newIsLeft)
        isNotchedRef.current = false
        setNotched(false)

        requestAnimationFrame(() => {
          releasingFromNotch = false
          heldByNotchRef.current = false
          const pillRect = contentRef.current?.getBoundingClientRect()
          const elW = pillRect?.width || PILL_SIZE
          const elH = pillRect?.height || PILL_SIZE
          elementSizeRef.current = { width: elW, height: elH }

          const handleX = newIsLeft ? ev.clientX - HANDLE_INSET : ev.clientX - elW + HANDLE_INSET
          const x = Math.max(0, Math.min(handleX, window.innerWidth - elW))
          const y = Math.max(0, Math.min(ev.clientY - elH / 2, window.innerHeight - elH))
          grabPointRef.current = { x: ev.clientX - x, y: ev.clientY - y }
          motionX.set(toMotionX(x, elW))
          motionY.set(y)

          if (!isDraggingRef.current) {
            const releaseDisplayKey = holdDisplayKey()
            void sendOverlayDragPing({ force: true }).then((dropDisplayId) => {
              setStoredPosition({ x, y }, dropDisplayId)
              releaseDisplayKey()
            })
          }
        })
      }

      const onPointerMove = (ev: PointerEvent) => {
        // Skip duplicate positions
        if (ev.clientX === lastClientRef.current.x && ev.clientY === lastClientRef.current.y) return

        if (heldByNotchRef.current) {
          lastClientRef.current = { x: ev.clientX, y: ev.clientY }
          const pulled = Math.hypot(ev.clientX - dragOriginRef.current.x, ev.clientY - dragOriginRef.current.y)
          if (!releasingFromNotch && pulled >= NOTCH_EXIT_DISTANCE) {
            releaseFromNotch(ev)
          }
          return
        }

        const { width: elW, height: elH } = elementSizeRef.current

        // Calculate desired position: cursor minus grab offset (screen-left coordinate)
        let newX = ev.clientX - grabPointRef.current.x
        let newY = ev.clientY - grabPointRef.current.y

        // Clamp to window bounds so Mini can never leave the viewport
        const maxX = window.innerWidth - elW
        const maxY = window.innerHeight - elH
        newX = Math.max(0, Math.min(newX, maxX))
        newY = Math.max(0, Math.min(newY, maxY))

        // Adjust grab point when clamped - this prevents a "jump" when the cursor
        // moves back from the edge into unclamped territory
        grabPointRef.current.x = ev.clientX - newX
        grabPointRef.current.y = ev.clientY - newY

        updateSnapCandidate(isWithinNotchSnapZone(notchRegionRef.current, newY, newX + elW / 2))

        // Real-time flip detection with hysteresis to prevent thrashing
        const currentIsLeft = isOnLeftSideRef.current
        const flipThresholdToRight = window.innerWidth * FLIP_THRESHOLD_TO_RIGHT
        const flipThresholdToLeft = window.innerWidth * FLIP_THRESHOLD_TO_LEFT

        const shouldFlipToRight = currentIsLeft && ev.clientX > flipThresholdToRight
        const shouldFlipToLeft = !currentIsLeft && ev.clientX < flipThresholdToLeft

        if (shouldFlipToRight || shouldFlipToLeft) {
          const newIsLeft = !currentIsLeft
          isOnLeftSideRef.current = newIsLeft
          pendingFlipRef.current = true
          setIsOnLeftSide(newIsLeft)

          // Recalculate position so cursor stays on the drag handle after flip
          // When flipping to left: drag handle is on left, cursor should be at left edge + HANDLE_INSET
          // When flipping to right: drag handle is on right, cursor should be at right edge - HANDLE_INSET
          let flippedX: number
          if (newIsLeft) {
            // Cursor should be HANDLE_INSET from left edge of element
            flippedX = ev.clientX - HANDLE_INSET
          } else {
            // Cursor should be HANDLE_INSET from right edge of element
            flippedX = ev.clientX - elW + HANDLE_INSET
          }

          // Clamp to window bounds
          const maxX = window.innerWidth - elW
          flippedX = Math.max(0, Math.min(flippedX, maxX))

          // Update grab point for the new anchor side
          grabPointRef.current.x = ev.clientX - flippedX
          grabPointRef.current.y = ev.clientY - newY

          // After React re-render applies new CSS anchoring, update motionX
          requestAnimationFrame(() => {
            motionX.set(toMotionX(flippedX, elW))
          })

          lastClientRef.current = { x: ev.clientX, y: ev.clientY }
          motionY.set(newY)

          void sendOverlayDragPing()
          return
        }

        motionX.set(toMotionX(newX, elW))
        motionY.set(newY)

        lastClientRef.current = { x: ev.clientX, y: ev.clientY }

        void sendOverlayDragPing()
      }

      const onResize = () => {
        if (!isDraggingRef.current || heldByNotchRef.current) return

        // Recalculate element size after window resize (e.g. display switch)
        const resRect = contentRef.current?.getBoundingClientRect()
        if (resRect) {
          elementSizeRef.current = {
            width: resRect.width,
            height: resRect.height,
          }
        }

        const { width: elW, height: elH } = elementSizeRef.current

        // Convert current motionX back to screen-left for clamping
        const screenX = isOnLeftSideRef.current ? motionX.get() : window.innerWidth + motionX.get() - elW
        const curY = motionY.get()

        // Re-clamp position to new window bounds
        const clampedX = Math.max(0, Math.min(screenX, window.innerWidth - elW))
        const clampedY = Math.max(0, Math.min(curY, window.innerHeight - elH))
        motionX.set(toMotionX(clampedX, elW))
        motionY.set(clampedY)

        // Reset grab point to element center so the next pointermove
        // re-anchors smoothly after the coordinate system change
        grabPointRef.current = { x: elW / 2, y: elH / 2 }
      }

      // pointercancel (system dialog, screen lock) ends the drag the same way as pointerup
      const endDrag = () => {
        cleanup()

        isDraggingRef.current = false
        setDragging(false)

        // Mark as dragged to suppress click events, then reset after delay
        didDragRef.current = true
        setTimeout(() => {
          didDragRef.current = false
        }, 100)

        // A release mid-transition (`releasingFromNotch`) persists from its own frame instead
        const skipStore = heldByNotchRef.current
        const shouldSnap = snapCandidateRef.current
        heldByNotchRef.current = false
        updateSnapCandidate(false)

        if (shouldSnap) {
          isNotchedRef.current = true
          setNotched(true)
          void sendOverlayDragPing({ force: true })
        } else if (!skipStore) {
          const { width: elW } = elementSizeRef.current
          const curY = motionY.get()
          // Convert motionX back to screen-left coordinate for storage
          const screenX = isOnLeftSideRef.current ? motionX.get() : window.innerWidth + motionX.get() - elW

          // Held until the position below is stored: main's `overlay.displayId`
          // broadcast beats the ping reply, and an unheld key would resolve the
          // destination's last parked spot and spring Mini to it on the way.
          const releaseDisplayKey = holdDisplayKey()

          // Final ping catches the last position and answers with the display the
          // drop landed on.
          void sendOverlayDragPing({ force: true }).then((dropDisplayId) => {
            setStoredPosition({ x: screenX, y: curY }, dropDisplayId)
            releaseDisplayKey()
          })
        }

        // Restore hover if the pointer is still over Mini. Deferred a frame so a snap
        // has re-rendered into the notched shell before the hit-test.
        requestAnimationFrame(() => {
          const { x, y } = lastClientRef.current
          const under = document.elementFromPoint(x, y)
          if (under && contentRef.current?.contains(under)) {
            setHovered(true)
          }
        })
      }

      const onPointerUp = () => endDrag()
      const onPointerCancel = () => endDrag()

      const cleanup = () => {
        window.removeEventListener('pointermove', onPointerMove)
        window.removeEventListener('pointerup', onPointerUp)
        window.removeEventListener('pointercancel', onPointerCancel)
        window.removeEventListener('resize', onResize)
        cleanupDragRef.current = null
      }

      cleanupDragRef.current = cleanup
      window.addEventListener('pointermove', onPointerMove)
      window.addEventListener('pointerup', onPointerUp)
      window.addEventListener('pointercancel', onPointerCancel)
      window.addEventListener('resize', onResize)
    },
    [
      setDragging,
      setIsOnLeftSide,
      setNotched,
      setStoredPosition,
      setHovered,
      holdDisplayKey,
      motionX,
      motionY,
      toMotionX,
    ],
  )

  // Set drag controls ref for the drag handle to use
  useLayoutEffect(() => {
    miniDragControlsRef.current = { start: startDrag }
    return () => {
      miniDragControlsRef.current = null
    }
  }, [startDrag])

  const handleMouseEnter = () => isMountDelayComplete && !isDragging && setHovered(true)
  const handleMouseLeave = () => {
    // Don't unhover while menu is open (dropdown is in a portal outside Mini)
    if (isMountDelayComplete && !isMenuOpen) {
      setHovered(false)
      setContentHovered(false)
    }
  }
  const notchedShellHover = useMiniContentHover({
    leaveDelayMs: NOTCH_UNHOVER_DELAY_MS,
    onEnter: handleMouseEnter,
    onLeave: handleMouseLeave,
  })

  return (
    <MiniNotchRegionProvider value={notchRegion}>
      <motion.div
        className="pointer-events-none fixed inset-0 z-30"
        variants={miniContainerVariants}
        initial="hidden"
        animate="visible"
        transition={miniContainerTransition}
      >
        <AnimatePresence>{isSnapCandidate && <MiniNotchSnapRegion key="snap" />}</AnimatePresence>
        {/* One shell at a time (no exit crossfade) so `contentRef` always points at the live one */}
        {isNotched ? (
          // Zero-width shell at the notch center; `MiniNotch` hangs its halves off it.
          // Forced dark so the foreground reads against the pure-black pill in either theme.
          <motion.div
            key="notched"
            ref={contentRef}
            className={twMerge(
              'dark pointer-events-auto absolute top-0 w-0 select-none',
              isPaused && 'grayscale',
              className,
            )}
            style={{ left: notchRegion.centerX, transformOrigin: 'center top' }}
            variants={miniShellVariants}
            custom={isDragging}
            initial={false}
            animate="visible"
            transition={miniShellTransition}
            {...notchedShellHover}
            data-mini-notch-shell=""
          >
            <LayoutGroup>{children}</LayoutGroup>
          </motion.div>
        ) : (
          <motion.div
            key="pill"
            ref={contentRef}
            className={twMerge(
              'pointer-events-auto absolute w-fit select-none',
              'flex items-center gap-0.5 rounded-full p-1',
              'bg-background shadow-lg',
              isOnLeftSide ? 'flex-row' : 'flex-row-reverse',
              isPaused && 'bg-background-subtle grayscale',
              className,
            )}
            style={{
              left: isOnLeftSide ? 0 : 'auto',
              right: isOnLeftSide ? 'auto' : 0,
              top: 0,
              x: motionX,
              y: motionY,
              transformOrigin: isOnLeftSide ? 'left center' : 'right center',
            }}
            variants={miniShellVariants}
            custom={isDragging}
            initial="hidden"
            animate="visible"
            transition={miniShellTransition}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Interactive>
              <LayoutGroup>
                <MiniDragHandle />
                {children}
              </LayoutGroup>
            </Interactive>
          </motion.div>
        )}
      </motion.div>
    </MiniNotchRegionProvider>
  )
}

Mini.Content = MiniContent
Mini.Logo = MiniLogo
Mini.DragHandle = MiniDragHandle
Mini.Controls = MiniControls
Mini.FinishedControls = MiniFinishedControls
Mini.Menu = MiniMenu
Mini.Notch = MiniNotch

export { Mini }
